/**
 * Generate SHA-256 hash using Web Crypto API
 * @param data - Uint8Array data to hash
 * @returns Hex string of the hash
 */
export async function generateSHA256Hash(data: Uint8Array): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data.buffer as ArrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate SHA-256 hash from a string
 * @param data - String data to hash
 * @returns Hex string of the hash
 */
export async function generateSHA256HashFromString(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  return generateSHA256Hash(dataBuffer);
}

/**
 * Generate HMAC-SHA256 signature for file integrity verification
 * Uses Web Crypto API for secure hashing in browser environments
 *
 * @param file - File or Blob to generate HMAC for
 * @param secret - Secret key for HMAC generation
 * @returns Hex string of the HMAC signature
 */
export async function generateFileHMAC(file: File | Blob, secret: string): Promise<string> {
  const encoder = new TextEncoder();

  // 1. Import Key
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: { name: 'SHA-256' } },
    false,
    ['sign'],
  );

  // 2. Read File
  const fileData = await file.arrayBuffer();

  // 3. Generate HMAC
  const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, fileData);

  // 4. Convert to Hex
  return Array.from(new Uint8Array(signature))
    .map((b) => `00${b.toString(16)}`.slice(-2))
    .join('');
}

/**
 * Verify HMAC signature for file integrity
 *
 * @param file - File or Blob to verify
 * @param signature - Expected HMAC signature (hex string)
 * @param secret - Secret key used for HMAC generation
 * @returns Boolean indicating if verification passed
 */
export async function verifyFileHMAC(file: File | Blob, signature: string, secret: string): Promise<boolean> {
  const computedSignature = await generateFileHMAC(file, secret);
  return computedSignature === signature;
}
